/**
 * Telemetry attribution: which client sent a request and which app it is
 * (spec multi-product 011 OPS-3, §2, as amended by 021 G47 (1) and R0-32;
 * WP0.28.1).
 *
 * - `x-cova-client` is CRN-style, `<app>:<client>:<version>` (005 API-8,
 *   R0-32): `platform:web:3.0.0`, `bots:web:0.0.0`, `bots:desktop:0.0.0`,
 *   `platform:ios:3.2.1`, `platform:android:3.2.1`. Every client sends it.
 * - `cova.client` = the value's `<app>:<client>` pair when the pair is on the
 *   allowlist and the version is well formed, else `platform:web`. Never
 *   inferred from the host: rewritten app requests arrive at the platform
 *   host. An unlisted or malformed value falls through to the default, so a
 *   client cannot invent tag values and inflate metric series. The version is
 *   read only to check the shape and never becomes a label.
 * - `cova.app` on an RPC span = the pair's app: `bots` for `bots:web` and
 *   `bots:desktop`, else `platform`. Never a header naming the app (G30);
 *   agent spans take the run's agent product app instead (WP0.28.2).
 *
 * Telemetry only: no security rule reads either value.
 */
import type { CovaApp } from "@/domain/scope/cova-apps"

export const COVA_CLIENT_HEADER = "x-cova-client"

/**
 * The allowlist of `<app>:<client>` pairs, each with its app. The iOS and
 * Android apps are listed before they send (they change on their own
 * repositories, R0-32), so their first release is attributed from day one.
 * `hub-web` is void: there is no admin host (021 G47 (1)). The closed set
 * keeps the metric series at these five.
 */
const CLIENT_APPS = {
  "platform:web": "platform",
  "bots:web": "bots",
  "bots:desktop": "bots",
  "platform:ios": "platform",
  "platform:android": "platform",
} as const satisfies Record<string, CovaApp>

export type CovaClient = keyof typeof CLIENT_APPS

export const COVA_CLIENTS = Object.keys(CLIENT_APPS) as ReadonlyArray<CovaClient>

export const DEFAULT_COVA_CLIENT: CovaClient = "platform:web"

const isCovaClient = (value: string): value is CovaClient => Object.hasOwn(CLIENT_APPS, value)

/** A semantic version, with an optional pre-release or build suffix. */
const VERSION = /^\d{1,9}\.\d{1,9}\.\d{1,9}(?:[-+][0-9a-z.-]{1,64})?$/

/** The `cova.client` value for a raw header: an allowlisted pair with a well-formed version, else the default. */
export const resolveCovaClient = (raw: string | null | undefined): CovaClient => {
  const parts = (raw?.trim().toLowerCase() ?? "").split(":")
  if (parts.length !== 3) return DEFAULT_COVA_CLIENT
  const [app, client, version] = parts as [string, string, string]
  const pair = `${app}:${client}`
  return isCovaClient(pair) && VERSION.test(version) ? pair : DEFAULT_COVA_CLIENT
}

/** The `cova.app` value of a client (OPS-3). */
export const appOfClient = (client: CovaClient): CovaApp => CLIENT_APPS[client]

/**
 * `cova.org_count_bucket` (OPS-3, R-011-2): how many firms the person is a
 * live member of, bucketed so a span or metric shows how multi-org people
 * behave without the count, the firms, or a series per person. Only these
 * three values are ever emitted.
 */
export const ORG_COUNT_BUCKETS = ["1", "2-3", "4+"] as const
export type OrgCountBucket = (typeof ORG_COUNT_BUCKETS)[number]

/** The bucket of a live membership count; none below one (no identity, nothing to bucket). */
export const orgCountBucketOf = (count: number): OrgCountBucket | null => {
  if (!Number.isFinite(count) || count < 1) return null
  if (count === 1) return "1"
  return count <= 3 ? "2-3" : "4+"
}
